import {
  validateMortgagePaymentAmount,
  validateRentAmountForSubmit,
} from './rentAmountValidators'
import { parseLocalDateString, validateDateOfBirth } from "@/lib/utils/dateValidation"
import { isUsCountry } from "../components/tabs/aboutYourselfTabHelpers"

const trim = (v: unknown): string => (v ?? "").toString().trim()

/** Angular: names must not have leading/trailing spaces (see About `validateName` parity). */
export function nameHasOuterWhitespace(value: unknown): boolean {
  const s = (value ?? "").toString()
  return s.length > 0 && s !== s.trim()
}

/** Angular `resident-application-add` aboutyourself first_name / last_name `Validators.pattern` */
export const ANGULAR_NAME_PATTERN =
  /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~-]+){0,2}(?<! )$/

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_DIGITS_MIN = 10
const ZIP_DIGITS = /^\d{5,6}$/
const MASKED_SSN = /^X{3}-X{2}-\d{4}$/i

export function angularFirstNameError(value: unknown): string | null {
  const raw = (value ?? "").toString()
  if (!raw.trim()) return "'First Name' is required."
  if (nameHasOuterWhitespace(raw)) return "First Name is not valid"
  const t = raw.trim()
  if (!ANGULAR_NAME_PATTERN.test(t)) return "First Name is not valid"
  return null
}

export function angularMiddleNameError(value: unknown): string | null {
  const raw = (value ?? "").toString()
  if (!raw.trim()) return null
  if (nameHasOuterWhitespace(raw)) return "Middle Name is not valid"
  if (!ANGULAR_NAME_PATTERN.test(raw.trim())) return "Middle Name is not valid"
  return null
}

export function angularLastNameError(value: unknown): string | null {
  const raw = (value ?? "").toString()
  if (!raw.trim()) return "'Last Name' is required."
  if (nameHasOuterWhitespace(raw)) return "Last Name is not valid"
  const t = raw.trim()
  if (!ANGULAR_NAME_PATTERN.test(t)) return "Last Name is not valid"
  return null
}

export function angularMobileError(value: unknown): string | null {
  const digits = trim(value).replace(/\D/g, "")
  if (!digits) return "'Mobile Phone' is required."
  if (digits.length < PHONE_DIGITS_MIN) {
    return "Enter Valid Phone Number. Should match pattern [+X(XXX) XXX-XXXX]."
  }
  return null
}

export function angularEmailError(value: unknown): string | null {
  const t = trim(value)
  if (!t) return "'Email Address' is required."
  if (!EMAIL_PATTERN.test(t)) return "'Email Address' is not valid."
  return null
}

export function angularCommunicationPreferenceError(value: unknown): string | null {
  if (!trim(value)) return "'Communication Preference' selection is required."
  return null
}

export function angularBirthdateError(value: unknown): string | null {
  const t = trim(value)
  if (!t) return "'Date of Birth' is required."
  const dob = validateDateOfBirth(t)
  if (!dob.isValid) {
    if (dob.error?.includes("18")) return "Applicant must be minimum 18 years old."
    if (dob.error?.includes("110")) return "Applicant cannot be older than 110 years."
    return dob.error ? `Date of Birth: ${dob.error}` : "'Date of Birth' is invalid."
  }
  return null
}

export function angularSsnError(value: unknown): string | null {
  const raw = trim(value)
  if (!raw) return "'SSN or ITIN' is required."
  if (MASKED_SSN.test(raw)) return null
  const digits = raw.replace(/\D/g, "")
  if (digits.length !== 9) {
    return "Enter Valid Social Security Number(SSN) or Individual Taxpayer Identification Number(ITIN). Should match pattern [XXX-XX-XXXX]."
  }
  return null
}

export function angularStreetNoError(value: unknown): string | null {
  const t = trim(value)
  if (!t) return "'Street No.' is required."
  return null
}

export function angularStreetNameError(value: unknown): string | null {
  if (!trim(value)) return "'Street Name' is required."
  return null
}

export function angularCityError(value: unknown): string | null {
  if (!trim(value)) return "'City' is required."
  return null
}

export function angularStateError(value: unknown): string | null {
  if (!trim(value)) return "'State' is required."
  return null
}

export function angularZipError(value: unknown): string | null {
  const digits = trim(value).replace(/\D/g, "")
  if (!digits) return "'Zipcode' is required."
  if (!ZIP_DIGITS.test(digits)) return "'Zipcode' must be 5 or 6 digits."
  return null
}

export function angularCountryError(value: unknown): string | null {
  if (!trim(value)) return "'Country' is required."
  return null
}

/** Matches `validateAlternateAddress` — skip validation until the user starts filling the row. */
export function alternateRowHasAnyField(addr: {
  street_address?: unknown
  house_no?: unknown
  street?: unknown
  city?: unknown
  state?: unknown
  zip_code?: unknown
}): boolean {
  return Boolean(
    trim(addr.street_address) ||
      trim(addr.house_no) ||
      trim(addr.street) ||
      trim(addr.city) ||
      trim(addr.state) ||
      trim(addr.zip_code),
  )
}

/** True when an alternate-address row would show inline validation errors. */
export function alternateAddressHasInlineErrors(addr: {
  street_address?: unknown
  house_no?: unknown
  street?: unknown
  city?: unknown
  state?: unknown
  zip_code?: unknown
  country?: unknown
  outside_us?: unknown
}): boolean {
  if (!alternateRowHasAnyField(addr)) return false
  const outsideUs =
    Boolean(addr.outside_us) || !isUsCountry(String(addr.country ?? "US"))
  return [
    angularAlternateStreetNoError(addr.house_no),
    angularStreetNameError(addr.street),
    angularCityError(addr.city),
    angularStateError(addr.state),
    angularAlternateZipError(addr.zip_code, outsideUs),
    angularCountryError(addr.country),
  ].some(Boolean)
}

export function angularAlternateStreetNoError(value: unknown): string | null {
  const t = trim(value)
  if (!t) return "'Street No.' is required."
  return null
}

export function angularAlternateZipError(
  value: unknown,
  outsideUs: boolean,
): string | null {
  const raw = trim(value)
  if (!raw) return "'Zipcode' is required."
  if (!outsideUs) {
    const digits = raw.replace(/\D/g, "")
    if (!ZIP_DIGITS.test(digits)) return "'Zipcode' must be 5 or 6 digits."
  }
  return null
}

export function angularAddressTypeError(value: unknown): string | null {
  if (!trim(value)) return "'Type of residence' is required."
  return null
}

export function angularMoveInError(value: unknown): string | null {
  if (!trim(value)) return "'Move In Date' is required."
  return null
}

function ymdFromInput(value: unknown): string {
  const t = trim(value)
  if (!t) return ""
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10)
  return ""
}

export function angularCurrentMoveOutDateError(
  value: unknown,
  opts: { addressType: string; stillHere: boolean; moveInDate?: unknown },
): string | null {
  if (opts.stillHere) return null
  if (!trim(value)) {
    const label =
      opts.addressType === "RENT" ? "Lease End Date" : "Planned Move Out Date"
    return `'${label}' is required.`
  }
  const ymdIn = ymdFromInput(opts.moveInDate)
  const ymdOut = ymdFromInput(value)
  if (ymdIn && ymdOut) {
    const dIn = parseLocalDateString(ymdIn)
    const dOut = parseLocalDateString(ymdOut)
    if (dIn && dOut && dOut.getTime() < dIn.getTime()) {
      const label =
        opts.addressType === "RENT" ? "Lease End Date" : "Planned Move Out Date"
      return `'${label}' must be on or after Move In Date.`
    }
  }
  return null
}

export function angularRentAmountError(value: unknown): string | null {
  return validateRentAmountForSubmit(value == null ? "" : String(value))
}

export function angularPaidRentMonthsError(_value: unknown): string | null {
  // Auto-calculated from move-in / move-out dates (see AddressEditForm).
  return null
}

export function angularMonthResideError(_value: unknown): string | null {
  // Auto-calculated from move-in / move-out dates (see AddressEditForm).
  return null
}

export function angularMortgagePaymentError(value: unknown): string | null {
  if (!trim(value)) return "'Monthly Mortgage Payment Amount' is required."
  return validateMortgagePaymentAmount(String(value))
}

function landlordLabel(
  addressType: string,
  _section: "current" | "previous",
): "Landlord" | "Contact" {
  // Applicant FamilyForm parity: FAMILY uses Contact on current and previous.
  return addressType === "FAMILY" ? "Contact" : "Landlord"
}

export function angularLandlordNameError(
  value: unknown,
  addressType: string,
  section: "current" | "previous",
): string | null {
  if (!trim(value)) {
    const who = landlordLabel(addressType, section)
    return `'${who} Name' is required.`
  }
  return null
}

export function angularLandlordEmailError(
  value: unknown,
  addressType: string,
  section: "current" | "previous",
): string | null {
  const requireEmail =
    addressType === "RENT" || section === "previous" || addressType === "FAMILY"
  if (!requireEmail) return null
  const t = trim(value)
  const who = landlordLabel(addressType, section)
  if (!t) return `'${who}' Email is required.`
  if (section === "previous" && !EMAIL_PATTERN.test(t)) {
    return `'${who}' Email — Enter Valid Email`
  }
  if (
    section === "current" &&
    (addressType === "FAMILY" || addressType === "RENT") &&
    !EMAIL_PATTERN.test(t)
  ) {
    return `'${who}' Email is not valid.`
  }
  return null
}

export function angularLandlordMobileError(
  value: unknown,
  addressType: string,
  section: "current" | "previous",
): string | null {
  const requirePhone =
    addressType === "RENT" || section === "previous" || addressType === "FAMILY"
  if (!requirePhone) return null
  const digits = trim(value).replace(/\D/g, "")
  const who = landlordLabel(addressType, section)
  if (!digits) return `'${who} Mobile' is required.`
  if (digits.length < PHONE_DIGITS_MIN) {
    return "Enter Valid Phone Number. Should match pattern [+X(XXX) XXX-XXXX]."
  }
  return null
}

export function angularContactRelationshipError(value: unknown): string | null {
  if (!trim(value)) return "'Contact Relationship' is required."
  return null
}
